/**
 * Team Agreements Overview Component
 *
 * Shows all open agreements across the team for a manager.
 * Displays agreements grouped by team member with action holder indicators.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTeamAgreements } from '../agreements/useAgreements';
import { AgreementDetailModal } from '../agreements/AgreementDetailModal';
import type { ScoreAgreement, ScoreAgreementStatus } from '../../lib/evaluationsApi';

const STATUS_LABELS: Record<ScoreAgreementStatus, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  definitief: { label: 'Definitief', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  afgerond: { label: 'Afgerond', color: 'text-green-700', bgColor: 'bg-green-100' },
  geannuleerd: { label: 'Geannuleerd', color: 'text-red-700', bgColor: 'bg-red-100' },
};

function isDeadlineUrgent(deadline: string | null): boolean {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= 7 && diffDays >= 0;
}

function isDeadlinePassed(deadline: string | null): boolean {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

type SortColumn = 'employeeName' | 'type' | 'deadline' | 'status' | 'actionHolder';
type SortDirection = 'asc' | 'desc';

export function TeamAgreementsOverview() {
  const { data, isLoading, error } = useTeamAgreements();
  const [selectedAgreement, setSelectedAgreement] = useState<ScoreAgreement | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('deadline');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Openstaande Team Afspraken</h3>
        </div>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Laden...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Openstaande Team Afspraken</h3>
        </div>
        <div className="p-6">
          <p className="text-red-600 text-sm">Er is een fout opgetreden bij het laden van de afspraken.</p>
        </div>
      </div>
    );
  }

  const agreements = data?.data || [];

  if (agreements.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Openstaande Team Afspraken</h3>
        </div>
        <div className="p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="mt-2 text-gray-500">Geen openstaande afspraken voor het team.</p>
        </div>
      </div>
    );
  }

  // Sort agreements based on selected column and direction
  const sortedAgreements = [...agreements].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;

    switch (sortColumn) {
      case 'employeeName': {
        const nameA = a.employeeName || '';
        const nameB = b.employeeName || '';
        return multiplier * nameA.localeCompare(nameB);
      }
      case 'type': {
        const typeA = a.agreementTypeName || '';
        const typeB = b.agreementTypeName || '';
        return multiplier * typeA.localeCompare(typeB);
      }
      case 'deadline': {
        // Null deadlines go to the end
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return multiplier * (new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
      }
      case 'status': {
        const statusOrder = { concept: 0, definitief: 1, afgerond: 2, geannuleerd: 3 };
        return multiplier * (statusOrder[a.status] - statusOrder[b.status]);
      }
      case 'actionHolder': {
        const aHolder = a.isEmployeeActionHolder ? 1 : 0;
        const bHolder = b.isEmployeeActionHolder ? 1 : 0;
        return multiplier * (aHolder - bHolder);
      }
      default:
        return 0;
    }
  });

  const urgentCount = agreements.filter(a => isDeadlinePassed(a.deadline) || isDeadlineUrgent(a.deadline)).length;
  const actionHolderCount = agreements.filter(a => a.isEmployeeActionHolder).length;

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-gray-900">
                  Openstaande Team Afspraken
                </h3>
                <span className="px-2 py-0.5 text-sm bg-blue-200 text-blue-800 rounded-full">
                  {agreements.length}
                </span>
                <Link
                  to="/team/afspraken"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Nieuwe afspraak maken"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Nieuw
                </Link>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Overzicht van alle openstaande afspraken in je team
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {urgentCount > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {urgentCount} urgent
                </span>
              )}
              {actionHolderCount > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {actionHolderCount} medewerker actiehouder
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center">
                    Medewerker
                    <SortIcon column="employeeName" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center">
                    Type
                    <SortIcon column="type" />
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Afspraak
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('deadline')}
                >
                  <div className="flex items-center">
                    Deadline
                    <SortIcon column="deadline" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon column="status" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('actionHolder')}
                >
                  <div className="flex items-center">
                    Actiehouder
                    <SortIcon column="actionHolder" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedAgreements.map((agreement) => {
                const statusConfig = STATUS_LABELS[agreement.status];
                const urgent = isDeadlineUrgent(agreement.deadline);
                const passed = isDeadlinePassed(agreement.deadline);

                return (
                  <tr
                    key={agreement.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedAgreement(agreement)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 text-sm font-medium">
                            {agreement.employeeName?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{agreement.employeeName || 'Onbekend'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center gap-1">
                        {agreement.agreementTypeIcon && <span>{agreement.agreementTypeIcon}</span>}
                        <span className="text-gray-900">{agreement.agreementTypeName || 'Onbekend'}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={agreement.beschrijving}>
                      {agreement.beschrijving}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {agreement.deadline ? (
                        <span className={`flex items-center gap-1 ${passed ? 'text-red-600 font-medium' : urgent ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                          {passed && <span>⚠️</span>}
                          {urgent && !passed && <span>⏰</span>}
                          {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {agreement.isEmployeeActionHolder ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          Medewerker
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          Leidinggevende
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedAgreement && (
        <AgreementDetailModal
          agreementId={selectedAgreement.id}
          onClose={() => setSelectedAgreement(null)}
          canEdit={true}
        />
      )}
    </>
  );
}
